import { defineStore } from 'pinia';
import api from '@/api/auth';
import { Session } from '@/utils/storage';
import storesAppConfig from '@/store/appConfig';
import invoiceAuth from '@/store/invoiceAuth';
import conf from '@/conf/config.json';
import { ElMessage } from 'element-plus';
import commonQyApi from '@/api/saas/qy/common';
import { commonQyApi as commonGroupApi } from '@/api/group/xtgl/swgl';
import invoiceApi from '@/api/invoice/qy';
import mittBus from '@/utils/mitt';
import util from '@/utils/util';
import { useMessage } from '@/store/message';
import { BizChannelEnum } from '@/constants/constants';
import { getRefererAppId } from '@/api/eTaxLogin/util';
const messageState = useMessage();

// 登录完整流程：账号登录 -> 租户检查 -> 租户绑定 -> 获取用户信息 -> 获取权限信息
export const useAccount = defineStore('account', {
	state: (): AccountState => ({
		account: undefined,
		timerId: undefined,
		tokenInfo: undefined, // token完整信息
		tenantCheck: true, // 租户检查结果
		tenantList: [], // 租户列表
		companiesList: [], //公司列表
		companiesId: undefined, //公司id
		companiseInfo: {}, //当前公司信息
		addCentObj: {},
		menuList: [], // 菜单权限
		authBtnList: [], // 操作权限
		whiteList: [
			'/h5',
			'/ETaxLogin',
			'/mobile/qysjbg',
			'/mobile/qyrz',
			'/mobile/jsbg',
			'/mobile/qysjbglb',
			'/mobile/qybgfx',
			'/websiteForm',
		], // 白名单
		clientDialogVisible: false, // 客户信息弹窗
		isClientDialog: false, // 是否是客户信息弹窗
		appId: '', // 当前应用标识
		bizChannel: BizChannelEnum.RPA, // 业务渠道
		isOutputSysAdmin: false, // 是否是销项子系统管理员
	}),
	actions: {
		/* 获取用户信息 */
		async getAccount() {
			if (this.matchWhiteList(location.pathname)) return;

			const res = await api.account();
			const resData = {
				id: res.data?.id,
				accountId: res.data?.accountId,
				orgCode: res.data.org?.code,
				tyshxydm: res.data.tyshxydm,
				orgId: res.data.org?.id,
				orgName: res.data.org?.name,
				skssq: res.data?.skssq,
				haveConsole: res.data?.haveConsole,
				username: res.data?.username,
				authBtnList: ['btn.add', 'btn.del', 'btn.edit', 'btn.link'], // 按钮权限标识
				nickname: res.data.nickname,
				accountType: res.data.accountType,
				realName: res.data.realName,
				avatar: res.data.avatar,
			};

			this.account = resData as any;
			this._checkIsOutputSysAdmin(res.data.roles);

			await this.stopCheckToken();
			// await this.startCheckToken();
		},

		/* 检查是否是销项子系统管理员 */
		_checkIsOutputSysAdmin(roles: any[]) {
			if (!roles) {
				this.isOutputSysAdmin = false;
				return;
			}
			const roleCodes = roles.map((role: any) => role.roleCode);
			// kpzxtgly: 开票子系统管理员
			this.isOutputSysAdmin = roleCodes.includes('kpzxtgly');
		},

		/* 定时检测 */
		async startCheckToken() {
			if (this.matchWhiteList(location.pathname)) return;

			//每分钟检测一次
			this.timerId = setInterval(() => {
				const token: AccessToken = Session.get('access_token');
				const diff = Date.parse(token.expireAt) - Date.now();
				if (diff < 5 * 60 * 1000) {
					//每分钟检测一次
					api.refresh(token.refreshToken).then((res: { data: AccessToken }) => {
						const tk: AccessToken = res.data;
						Session.set('token', tk.accessToken);
						Session.set('access_token', tk);
					});
				}
			}, 1000 * 60);
		},
		async stopCheckToken() {
			if (this.timerId) clearInterval(this.timerId);
		},

		/* 设置token信息 */
		async setAccountToken(data: AccessToken) {
			this.tokenInfo = data;
		},

		/* 将token缓存到浏览器中 */
		async sessionToken() {
			Session.set('token', this.tokenInfo.accessToken);
			//Session.set('access_token', this.tokenInfo);
		},

		/* 获取租户检查结果 */
		async getCheckTenant() {
			this.tenantCheck = true;
			// 1.白名单：不需要租户检查
			if (this.matchWhiteList(location.pathname)) return this.tenantCheck;
			// 2.获取租户检查结果
			const { data } = await api.checkTenant();
			this.tenantCheck = data;

			// 2.1 true 获取用户信息、获取权限信息
			if (data) {
				await this.getAccount();
				await this.getPermiss();
				return this.tenantCheck;
			}

			// 2.2 false 绑定租户
			await this.getTenantList();
			return this.tenantCheck;
		},

		/* 获取租户列表 */
		async getTenantList() {
			const { data } = await api.getTenant();
			this.tenantList = data;
			// 如果账号所属租户只有一个，则自动绑定
			if (data.length === 1) await this.bindTenant(data[0].accountId, data[0].orgId);
		},

		/* 绑定租户 */
		async bindTenant(accountId: string, orgId: string) {
			await api.setTenant({ accountId, orgId });

			// 获取用户信息
			await this.getAccount();
			// 获取权限信息
			await this.getPermiss();

			// 无异常，跳转至正确页面
			let redirect = '';
			// 重定向到设置的默登录应用
			const loginAppId = Session.get('loginAppId');
			if (loginAppId) {
				redirect = conf.pages[loginAppId];
			} else {
				redirect = storesAppConfig.code ? conf.pages[storesAppConfig.code] : 'login.html';
			}

			const paths = redirect.split('.');
			const tabName = paths ? paths[0] : '_blank';
			if (!window.opener) {
				// 如果当前窗口没有opener，则直接刷新页面
				window.name = tabName;
				location.href = location.origin + '/' + redirect;
			} else if (window.name === tabName) {
				// 如果跳转的目的窗口==当前窗口直接刷新页面
				location.href = location.origin + '/' + redirect;
			} else {
				// 如果跳转的目的窗口!=当前窗口，关闭当前窗口，打开新窗口
				window.close();
				util.openUniqueTab('/' + redirect, tabName);
			}
		},

		/* 根据账号id获取授权企业信息 */
		// async getEnterpriseByAccountId(id: any) {
		//   const { data } = await accountApi.queryEntListByAccountId(id);
		//   this.companiesList = data.list.map((item: any) => {
		//     if (!('id' in item)) item.id = item.tyshxydm
		//     return item
		//   })
		//   if (!data.list.length) {
		//     ElMessage.error('未获取到授权企业信息')
		//     return
		//   }

		//   this.changeAccountId(Session.get('entId') || null)
		//   return data.list
		// },

		async getEnterpriseList() {
			let queryUserQyList;
			if (getRefererAppId() === 'main_group') {
				queryUserQyList = commonGroupApi.queryUserQyList;
			} else {
				queryUserQyList = commonQyApi.queryUserQyList;
			}
			if (this.companiesList.length) {
				// await this.changeAccountId(Session.get('entId') || null);
				return this.companiesList;
			}
			const { data } = await queryUserQyList();
			if (!data.length) {
				ElMessage.error('未获取到授权企业信息');
				mittBus.emit('noEnterprise');
				return;
			}
			this.companiesList = data;
			await this.changeAccountId(Session.get('entId') || null);
			return data;
		},

		/* 更新授权企业id */
		async changeAccountId(id: string | null) {
			const current = this.companiesList.find((item: any) => item.id === id);
			if (current) {
				this.companiesId = current.id;
				this.companiseInfo = current;
				window.companyType = current.nsrlx;
			} else {
				this.companiesId = this.companiesList[0].id;
				this.companiseInfo = this.companiesList[0];
				window.companyType = this.companiesList[0].nsrlx;
			}

			await invoiceApi.changeQyId(this.companiesId);
			Session.set('entId', this.companiesId); // 机构id
			mittBus.emit('switchCompanyComplete');
			if (storesAppConfig.code === 'sub_invoice') {
				// 获取用户开票授权数据
				invoiceAuth.getAuth();
				messageState.setMessageNumber();
			}
			return this.companiesId;
		},

		/* 保存客户信息 */
		changeAddCentObj(obj: any) {
			this.addCentObj = obj;
		},

		/* 获取当前租户权限 */
		async getPermiss() {
			const { data } = await api.getPermissionData();
			if (!data.menus.length) {
				ElMessage.error('无菜单权限');
				return Promise.reject(true);
			}
			this.appId = data.appId;
			this.menuList = data.menus;
			this.authBtnList = data.authBthList;
			return data.menus;
		},

		/* 白名单匹配 */
		matchWhiteList(path: string) {
			if (this.whiteList.includes(path)) return true;
			else return false;
		},

		// 打开下载客户端弹窗
		openClientDialog(isClient: boolean = false) {
			this.clientDialogVisible = true;
			this.isClientDialog = isClient;
		},

		// 设置业务渠道
		setBizChannel(channel: BizChannelEnum) {
			this.bizChannel = channel;
			window.bizChannel = channel;
		},
	},
});
